// =========================================================================
// Checking a card before the Ledger is asked — in the dashboard, with the
// signing page's own functions, in the signing page's own order.
//
// cosign/src/main.ts does this inline on the page: decode the signed bytes,
// recompute the id, pin the token contract to this build's network, hold the
// server's claims against the bytes, place the recipient against what this
// device confirmed, and refuse structurally on anything that disagrees. This
// module is that sequence as a pure function, so the dashboard's cable signer
// (CableSign.tsx) shows the same facts and refuses the same cards — and so the
// sequence has tests of its own. The functions are the page's (lib/ledger-cable
// re-exports cosign/src/verify.ts); nothing here decides what a transaction
// means by itself.
//
// What it cannot do is make the screen trustworthy. That is what the Ledger's
// own display is for, and the sheet says so.
// =========================================================================

import {
  decodeForDisplay, usdtContract, crossCheck, parseDestination, parseSigner, anchorRecipient,
  anchorBlocksSigning, anchorIsOverridable, showsEnding, maskTail, unitsToDecimal, groupTxId, kindMismatch, deviceProof,
  MAX_COSIGN_WINDOW_MS,
  type Decoded, type Anchor, type Network, type ConfirmedDestination, type EntryKind,
} from '../../lib/ledger-cable';
import type { SigningCard } from '../../lib/api';

const NETWORK_NAME: Record<Network, string> = { mainnet: 'TRON mainnet', nile: 'the Nile test network' };

/** What the sheet shows, all of it decoded from the signed bytes — except the names, which are the desk's. */
export interface CardFacts {
  kind: string;
  /** USDT, from the bytes. */
  amount: string;
  /** TRX, from the bytes. */
  feeLimit: string;
  /** The recipient, from the bytes — in full. */
  to: string;
  /** The recipient as it may be SHOWN: the ending hidden until the device knows it or the operator has typed it. */
  toShown: string;
  /** The wallet the money leaves, from the bytes. */
  from: string;
  /** Its name, from the desk — or "not named by the desk". */
  fromName: string;
  permission: number;
  /** When the transaction stops being signable, epoch ms; null when it carries no expiry. */
  closesAt: number | null;
  txId: string;
  /** The id in eight-character groups, to compare with a device. */
  txIdGroups: string[];
  network: Network;
}

export interface CardCheck {
  /** Everything that stops the ceremony, as sentences. Empty when the card is coherent. */
  problems: string[];
  decoded: Decoded | null;
  facts: CardFacts | null;
  /** Where the recipient stands against the desk's entry and this device's list; null when the card could not be read. */
  anchor: Anchor | null;
  /** The key the device must sign with — or null when no key the operator holds can (the server says so). */
  signer: string | null;
  /** The register's path for the source wallet, handed to the cable signer to try first. */
  derivation: string | null;
  /** A desk destination the Ledger must derive before it signs (cosign/src/ledger.ts `prove`); null when there is nothing to prove. */
  prove: { account: number; derivation: string; expect: string; name: string } | null;
  /** The register's layer of a desk destination (what a typed record of it is kept under); null for a counterparty or no entry. */
  destinationLayer: string | null;
  /** The address must be typed (its ending) before signing: nothing else is in the way. */
  mustConfirm: boolean;
  /** Nothing stands in the way of asking the device. */
  ready: boolean;
}

export interface CheckInput {
  /** The id the operator opened — the card's own, from the queue. */
  txid: string;
  network: Network;
  /** This device's clock, epoch ms. */
  now: number;
  /** loadConfirmed(network): what this device confirmed before. */
  confirmed: ConfirmedDestination[];
}

const refusedOutright = (problems: string[]): CardCheck => ({
  problems, decoded: null, facts: null, anchor: null, signer: null, derivation: null, prove: null, destinationLayer: null, mustConfirm: false, ready: false,
});

/** Check one card. Never throws: a card that cannot be read is a check with problems. */
export function checkCard(card: SigningCard, input: CheckInput): CardCheck {
  const { network, now } = input;
  const txid = input.txid.toLowerCase();

  if (!card || typeof card !== 'object' || typeof card.rawDataHex !== 'string') {
    return refusedOutright(['The server answered with nothing this screen can read.']);
  }

  // ---- decode LOCALLY, from the bytes the signature covers ---------------
  let decoded: Decoded;
  try {
    decoded = decodeForDisplay(card.rawDataHex);
  } catch (err) {
    return refusedOutright([
      `This transaction could not be decoded: ${String((err as Error)?.message ?? err)}. Not signable.`,
    ]);
  }

  const problems: string[] = [];

  if (decoded.contract !== usdtContract(network)) {
    const other: Network = network === 'mainnet' ? 'nile' : 'mainnet';
    if (decoded.contract === usdtContract(other)) {
      // The other network's USDT: the app and the desk set to different
      // networks — a set-up fault, not a forged transaction.
      problems.push(`This app and this transaction are on different networks: USDT on ${NETWORK_NAME[other]}, an app built for ${NETWORK_NAME[network]}. A set-up fault — report it.`);
    } else {
      problems.push(`Unrecognised token contract ${decoded.contract}: not USDT on ${NETWORK_NAME[network]}. Refusing.`);
    }
  }
  if (decoded.txID.toLowerCase() !== txid) {
    problems.push(`The transaction does not match its id: the queue names ${txid.slice(0, 16)}…, the bytes hash to ${decoded.txID.slice(0, 16)}…. Tampering, not a glitch.`);
  }

  // ---- place the recipient against what was confirmed on this device ------
  const entry = parseDestination(card.destination);
  const anchor = anchorRecipient(decoded.to, entry, input.confirmed);
  // The destination's ending is on screen only for a destination this device
  // already knows, or once the operator has typed it. A desk wallet the
  // Ledger proves when it signs is not typed, so there is nothing to hide.
  const showRecipient = anchor.state === 'known' || anchor.state === 'desk' ? (address: string) => address : maskTail;
  // The card's two labels must agree: its kind, and where the money goes.
  const mismatch = kindMismatch(card.kind, entry);
  if (mismatch) problems.push(mismatch);

  // ---- cross-check the server's own summary ------------------------------
  const sourceAddress = typeof card.source?.address === 'string' ? card.source.address : undefined;
  const disagreements = crossCheck(decoded, { to: card.to, amount: card.amount, from: sourceAddress }, showRecipient);
  if (disagreements.length) {
    const uncomparableOnly = disagreements.every((d) => d.includes('not a value this page can compare'));
    problems.push(uncomparableOnly
      ? `The server's summary cannot be checked against the signed bytes: ${disagreements.join('; ')}. Report this.`
      : `The server's summary disagrees with the signed bytes: ${disagreements.join('; ')}. Do not sign — report this.`);
  }

  // Signable for at most an hour by this device's clock — the bound the signing page holds through assertCosignSafe.
  // The sheet never called it, so a card with no expiry, or one far ahead, passed (audit of 21 Oct 2026, E5).
  const expired = decoded.expiration !== null && decoded.expiration <= now;
  if (decoded.expiration === null) {
    problems.push('This transaction never expires. Do not sign — report this.');
  } else if (expired) {
    problems.push('This transaction has expired. Rebuild it; do not extend it.');
  } else if (decoded.expiration - now > MAX_COSIGN_WINDOW_MS) {
    problems.push(`This transaction stays signable for more than ${MAX_COSIGN_WINDOW_MS / 60_000} minutes. Do not sign — report this.`);
  }

  // ---- the wallet the money leaves ----------------------------------------
  const fromName = typeof card.source?.name === 'string' && card.source.name && sourceAddress === decoded.owner
    ? card.source.name
    : 'not named by the desk';
  // A sweep empties a deposit address. A card called a sweep that spends from
  // anything else is the desk's own two labels disagreeing (R24).
  if (card.kind === 'sweep' && card.source?.layer !== 'deposit') {
    problems.push(`This card is called a sweep, but the money does not leave a deposit address — it leaves ${fromName}. Do not sign; report this.`);
  }

  // ---- whose key signs ----------------------------------------------------
  const parsedSigner = parseSigner(card.signer);
  const signer = parsedSigner !== null && parsedSigner !== 'unreadable' ? parsedSigner.address : null;
  if (parsedSigner === null) {
    problems.push(`No key you hold can sign this card. The money is in ${fromName}: either that wallet moves only with its own key and it is not the Ledger registered to you, or no Ledger is registered to you. Report this card.`);
  } else if (parsedSigner === 'unreadable') {
    problems.push('The desk did not say which key signs this card. This app and the desk\'s server are out of step — report this.');
  } else if (card.kind === 'sweep' && signer !== decoded.owner) {
    problems.push('This sweep asks to be signed by a key that is not the deposit address\'s own. A deposit address moves only with its own key, so the network would refuse it. Report this.');
  }

  const amount = unitsToDecimal(decoded.amount, 6);
  const feeLimit = unitsToDecimal(decoded.feeLimit, 6);

  // ---- the ending must not be on screen beside the box that asks for it ----
  if (anchorIsOverridable(anchor) && showsEnding([
    'partyName' in anchor ? anchor.partyName : '', 'detail' in anchor ? anchor.detail : '',
    'heldElsewhere' in anchor ? anchor.heldElsewhere ?? '' : '',
    String(card.kind), amount, fromName, decoded.owner, String(decoded.permissionId), feeLimit,
    decoded.expiration === null ? '' : String(decoded.expiration), decoded.txID,
  ], decoded.to)) {
    problems.push('This card shows the characters you would be asked to type. That defeats the check of the address. Do not sign — report this.');
  }

  const derivation = typeof card.source?.derivation === 'string' && card.source.derivation ? card.source.derivation : null;
  const refusing = problems.length > 0;
  const mustConfirm = !refusing && anchorIsOverridable(anchor);

  return {
    problems,
    decoded,
    facts: {
      kind: String(card.kind),
      amount, feeLimit,
      to: decoded.to,
      toShown: showRecipient(decoded.to),
      from: decoded.owner, fromName,
      permission: decoded.permissionId,
      closesAt: decoded.expiration === null || expired ? null : decoded.expiration,
      txId: decoded.txID,
      txIdGroups: groupTxId(decoded.txID),
      network,
    },
    anchor,
    signer,
    derivation,
    prove: refusing ? null : deviceProof(anchor, decoded.to),
    destinationLayer: entry?.kind === 'desk' ? entry.layer : null,
    mustConfirm,
    ready: !refusing && !anchorBlocksSigning(anchor),
  };
}

/**
 * The anchor, as a sentence — the page's banners (cosign/src/main.ts
 * anchorBanner), for the sheet. `canConfirm` says whether the typing box is
 * on screen, so "below" is said only when there is something below.
 *
 * Null when there is nothing to say: a move to one of the desk's own wallets
 * at the address this device last signed to is the ordinary case, and a
 * banner over every ordinary card teaches the eye to skip banners (the
 * operator, 18 Oct 2026: "too clumsy and complex").
 */
export function anchorSentence(a: Anchor, canConfirm: boolean): { tone: 'ok' | 'notice' | 'danger'; text: string } | null {
  const next = canConfirm ? 'Sign after you confirm its address below.' : 'Not signable here.';
  switch (a.state) {
    case 'known':
      return a.kind === 'desk' ? null : { tone: 'ok', text: `Recipient is ${a.partyName}, confirmed on this device.` };
    case 'desk':
      return null;   // proven on the Ledger when it signs; nothing to say
    case 'unconfirmed':
      return { tone: 'danger', text: a.heldElsewhere
        ? `${a.partyName} is a name this device already holds for another address, ${a.heldElsewhere}. ${next}`
        : `${a.partyName} has not been confirmed on this device. ${next}` };
    case 'changed':
      return { tone: 'danger', text: `Changed: the desk now calls this address ${a.partyName}, and ${a.detail}. ${next}` };
    case 'unapproved':
      return { tone: 'danger', text: `${a.partyName} is not approved in the Books. Approve the entry there first.` };
    case 'unpayable':
      return { tone: 'danger', text: `${a.partyName} is not a wallet a signed transfer may pay. Report this.` };
    case 'unknown':
      return { tone: 'danger', text: 'The desk has no payable entry for this destination. Report this card; do not register the address yourself.' };
  }
}

/** How the operator is told to find the address he must type — from a record that is not this screen. */
export function confirmInstruction(a: { partyName: string; kind: EntryKind }, tail: number): string {
  return a.kind === 'desk'
    ? `Find ${a.partyName} by name in the desk's own record of its wallets, and type the last ${tail} characters of the address recorded there.`
    : `Find it in ${a.partyName}'s own confirmation of their address — not on this screen, and not on the channel the address arrived on. Type its last ${tail} characters.`;
}
